import { useState } from "react";
import { Copy, Terminal } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL } from "@/lib/api";

const BASE = `${BACKEND_URL}/api/v1`;

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
  }
}

const ENDPOINTS = [
  {
    id: "balance",
    method: "GET",
    path: "/v1/balance",
    desc: "Returns the reseller's per-platform diamond balances.",
    curl: `curl -s "${BASE}/balance" \\
  -H "X-API-Key: YOUR_API_KEY"`,
    resp: `{
  "balances": {
    "trumeet":  { "allocated": 500000, "used": 120000, "balance": 380000, "name": "Trumeet" },
    "olaparty": { "allocated": 1000000, "used": 0, "balance": 1000000, "name": "Qwick Live" }
  }
}`,
  },
  {
    id: "lookup",
    method: "GET",
    path: "/v1/lookup/{recipient_id}?platform=olaparty",
    desc: "Verify a recipient's in-app ID before recharging. Returns their uid, nick and avatar.",
    curl: `curl -s "${BASE}/lookup/2095978736?platform=olaparty" \\
  -H "X-API-Key: YOUR_API_KEY"`,
    resp: `{ "uid": "50012345", "vid": "2095978736", "nick": "PlayerName", "avatar": "https://..." }`,
  },
  {
    id: "recharge",
    method: "POST",
    path: "/v1/recharge",
    desc: "Transfer diamonds to a recipient. Deducts the typed amount from your balance. On Qwick Live the recipient receives amount × 200.",
    curl: `curl -s -X POST "${BASE}/recharge" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "recipient_uid": "2095978736",
    "diamonds": 100,
    "platform": "olaparty",
    "note": "order #123"
  }'`,
    resp: `{
  "id": "...",
  "status": "success",
  "diamonds": 100,
  "transferred": 20000,
  "multiplier": 200,
  "recipient": { "uid": "50012345", "nick": "PlayerName" }
}`,
  },
  {
    id: "noble",
    method: "POST",
    path: "/v1/noble",
    desc: "Activate or renew a noble title (Qwick Live only). tier = knight | viscount | earl | duke, mode = first | renew.",
    curl: `curl -s -X POST "${BASE}/noble" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "recipient_uid": "2095978736",
    "tier": "knight",
    "mode": "first",
    "platform": "olaparty"
  }'`,
    resp: `{
  "id": "...",
  "status": "success",
  "type": "noble",
  "tier": "knight",
  "tier_label": "Knight",
  "mode": "first",
  "diamonds": 260,
  "recipient": { "uid": "50012345", "nick": "PlayerName" }
}`,
  },
];

function CodeBlock({ code, testid }) {
  const copy = async () => {
    await copyText(code);
    toast.success("Copied");
  };
  return (
    <div className="relative group">
      <pre className="bg-zinc-950 text-zinc-100 rounded-lg p-4 text-xs overflow-x-auto font-mono leading-relaxed" data-testid={testid}>
        {code}
      </pre>
      <button
        onClick={copy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-zinc-800 text-zinc-300 hover:bg-zinc-700 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Copy"
      >
        <Copy className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

const METHOD_COLORS = {
  GET: "bg-emerald-100 text-emerald-800",
  POST: "bg-blue-100 text-blue-800",
};

export default function ApiDocs() {
  const [copied, setCopied] = useState(false);
  const copyBase = async () => {
    await copyText(BASE);
    setCopied(true);
    toast.success("Base URL copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="api-docs-page">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Developer</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Reseller API</h1>
        <p className="text-zinc-500 mt-1">
          Share these docs with resellers so they can push recharges from their own systems using their API key.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4">
        <div>
          <h2 className="font-display text-lg font-medium text-zinc-900">Authentication</h2>
          <p className="text-sm text-zinc-600 mt-1">
            Every request must include the reseller's API key in the{" "}
            <code className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">X-API-Key</code> header
            (or as <code className="font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">Authorization: Bearer &lt;key&gt;</code>).
            Generate a key from <span className="font-medium">Resellers → (select reseller) → API Key</span>.
          </p>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Base URL</div>
          <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
            <Terminal className="w-4 h-4 text-zinc-400 flex-shrink-0" />
            <code className="flex-1 font-mono text-sm text-zinc-800 break-all" data-testid="api-base-url">{BASE}</code>
            <button onClick={copyBase} className="p-2 rounded-md hover:bg-zinc-200 text-zinc-600" title="Copy">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {copied && <div className="text-xs text-emerald-600 mt-1">Copied!</div>}
        </div>
        <div className="text-sm text-zinc-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
          The API enforces the same rules as the portal: per-platform balance limits, disabled-account checks,
          test-mode, and the Qwick Live 200× multiplier. Failed upstream calls automatically refund diamonds.
        </div>
      </div>

      {ENDPOINTS.map((e) => (
        <div key={e.id} className="bg-white border border-zinc-200 rounded-lg p-6 space-y-3" data-testid={`endpoint-${e.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs font-bold px-2 py-0.5 rounded ${METHOD_COLORS[e.method]}`}>{e.method}</span>
            <code className="font-mono text-sm text-zinc-900">{e.path}</code>
          </div>
          <p className="text-sm text-zinc-600">{e.desc}</p>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Request</div>
            <CodeBlock code={e.curl} testid={`curl-${e.id}`} />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1">Example response</div>
            <CodeBlock code={e.resp} testid={`resp-${e.id}`} />
          </div>
        </div>
      ))}
    </div>
  );
}
